import { Controller } from '@hotwired/stimulus'

interface RiskScore { severity_level: string, color: string, value?: number }

export default class RiskAssessmentController extends Controller {
  static targets: string[] = [
    '_form', 'probability', 'impact', 'value', 'category', 'box',
    'sharedCell', 'sharedBox', 'sharedCategory', 'sharedValue'
  ]

  static values = { riskId: Number }
  static outlets = ['risk-assessment']

  declare readonly impactTarget: HTMLInputElement
  declare readonly probabilityTarget: HTMLInputElement
  declare readonly valueTarget: HTMLElement
  declare readonly categoryTarget: HTMLElement
  declare readonly boxTarget: HTMLElement
  declare readonly hasSharedCellTarget: boolean
  declare readonly sharedCellTarget: HTMLElement
  declare readonly sharedBoxTarget: HTMLElement
  declare readonly sharedCategoryTarget: HTMLElement
  declare readonly sharedValueTarget: HTMLElement
  declare readonly riskAssessmentOutlets: RiskAssessmentController[]
  declare readonly riskIdValue: number
  riskId: number | null = null
  lastSharedScore: RiskScore | null | undefined = undefined

  async connect (): Promise<void> {
    const riskIdValue = this.riskIdValue
    const hasRiskIdValue: boolean = Number.isFinite(riskIdValue) && riskIdValue !== 0
    this.riskId = Number(window.location.pathname.split('/')[2])

    if (hasRiskIdValue) {
      this.riskId = riskIdValue
    }

    await this.updateScore()
  }

  async updateScore (): Promise<void> {
    await this.fetchRiskColor()
  }

  async fetchRiskColor (): Promise<void> {
    try {
      if (this.riskId === null) {
        throw new Error('Risk ID is null')
      }

      const impact: number = parseInt(this.impactTarget.value)
      const probability: number = parseInt(this.probabilityTarget.value)

      const response = await fetch(`/risks/${this.riskId}/assessments/risk_color?impact=${impact}&probability=${probability}`)

      if (!response.ok) {
        throw new Error('Failed to fetch risk color data')
      }

      const data: { severity_level?: string, color?: string, value?: number } = await response.json()

      if (typeof data.severity_level === 'string' && typeof data.color === 'string') {
        this.valueTarget.textContent = String(data.value)
        this.categoryTarget.textContent = `${data.severity_level}:`
        this.boxTarget.className = `${this.getBaseClasses()} ${data.color}`
        this.shareScore({ severity_level: data.severity_level, color: data.color, value: data.value })
      } else {
        this.resetRiskInfo()
      }
    } catch (error) {
      this.resetRiskInfo()
    }
  }

  resetRiskInfo (): void {
    this.valueTarget.textContent = ''
    this.categoryTarget.textContent = 'Very Low: 0'
    this.boxTarget.className = `${this.getBaseClasses()} bg-stm-teal-tinted`
    this.shareScore(null)
  }

  riskAssessmentOutletConnected (outlet: RiskAssessmentController): void {
    if (this.lastSharedScore !== undefined) outlet.syncSharedScore(this.lastSharedScore)
  }

  syncSharedScore (score: RiskScore | null): void {
    if (!this.hasSharedCellTarget) return

    this.sharedCellTarget.classList.toggle('hidden', score === null)
    this.element.toggleAttribute('data-two-scores', score !== null)
    if (score === null) return

    this.sharedValueTarget.textContent = String(score.value)
    this.sharedCategoryTarget.textContent = `${score.severity_level}:`
    this.sharedBoxTarget.className = `${this.getBaseClasses()} ${score.color}`
  }

  getBaseClasses (): string {
    return 'flex items-center px-4 py-2 space-x-1 rounded-lg h-full'
  }

  private shareScore (score: RiskScore | null): void {
    this.lastSharedScore = score
    this.riskAssessmentOutlets.forEach((outlet) => outlet.syncSharedScore(score))
  }
}
